import React from 'react';
import AnimatedSection from '../components/AnimatedSection';

const TermsPage = () => {
  const lastUpdated = 'December 2025';

  return (
    <div data-testid="terms-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-darkgreen mb-4">
              Terms of Service
            </h1>
            <p className="font-subheading text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="prose prose-lg max-w-none">
              <div className="space-y-12">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Agreement to Terms
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    By accessing or using the Green Palm Consulting website and services, you agree 
                    to be bound by these Terms of Service. If you do not agree to these terms, 
                    please do not use our website or services. We reserve the right to modify 
                    these terms at any time, and your continued use constitutes acceptance of 
                    any modifications.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Description of Services
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    Green Palm Consulting provides real estate purchasing services. We evaluate 
                    residential properties and make cash offers to homeowners looking to sell. 
                    Our services include property evaluation, cash offer presentation, and 
                    facilitation of the home sale process.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    No Obligation
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    Submitting your property information or receiving an offer from Green Palm 
                    Consulting does not create any obligation to sell. All offers are provided 
                    for informational purposes and are non-binding until a formal purchase 
                    agreement is executed by both parties.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    User Representations
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed mb-4">
                    By using our services, you represent and warrant that:
                  </p>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>You are at least 18 years of age</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>You have the legal authority to sell the property in question, or are acting on behalf of someone who does</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>All information you provide is accurate and truthful</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-emerald rounded-full mt-2 flex-shrink-0"></span>
                      <span>You will not use our services for any unlawful purpose</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Intellectual Property
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    All content on this website, including text, graphics, logos, images, and 
                    software, is the property of Green Palm Consulting and is protected by 
                    intellectual property laws. You may not reproduce, distribute, or create 
                    derivative works from our content without express written permission.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Limitation of Liability
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    To the fullest extent permitted by law, Green Palm Consulting shall not be 
                    liable for any indirect, incidental, special, consequential, or punitive 
                    damages arising from your use of our website or services. Our total liability 
                    shall not exceed the amount paid by you, if any, for using our services.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Indemnification
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    You agree to indemnify and hold harmless Green Palm Consulting, its officers, 
                    directors, employees, and agents from any claims, damages, losses, or expenses 
                    arising from your use of our services or violation of these Terms of Service.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Governing Law
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    These Terms of Service shall be governed by and construed in accordance with 
                    the laws of the United States. Any disputes arising from these terms shall 
                    be resolved in the appropriate courts of the jurisdiction where Green Palm 
                    Consulting maintains its principal place of business.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Severability
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    If any provision of these Terms of Service is found to be unenforceable or 
                    invalid, that provision shall be limited or eliminated to the minimum extent 
                    necessary, and the remaining provisions shall remain in full force and effect.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-bold text-darkgreen mb-4">
                    Contact Information
                  </h2>
                  <p className="font-body text-gray-600 leading-relaxed">
                    For questions about these Terms of Service, please contact us at:
                  </p>
                  <div className="mt-4 p-6 bg-gray-50 rounded-xl">
                    <p className="font-body text-gray-700">
                      <strong>Green Palm Consulting</strong><br />
                      30 N Gould St Ste R<br />
                      Sheridan, WY 82801<br />
                      Email: legal@greenpalmconsulting.com<br />
                      Phone: +1 307-298-2879
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
